/**
 * Cards per docs/REDESIGN.md 3.3: three render tiers chosen by width (mini,
 * small, large), illustrated cards for −5, 0..3 and 10..12, plain typeset
 * cards for 4..9, and a lapis-and-gold back built in code. An art card whose
 * illustration is missing falls back to the typeset layout with its name.
 */
import { memo, useState } from 'react';
import type { CSSProperties } from 'react';
import { srcSet, useAssets } from '../assets';
import { ART_VALUES, MOTTO, cardNameOf, fmt } from '../brand';
import { Shield, ShieldSvg } from './Art';

export type Tier = 'mini' | 'small' | 'large';

export function tierFor(width: number): Tier {
  return width < 40 ? 'mini' : width < 96 ? 'small' : 'large';
}

/**
 * Numeral color code: jade for −5..3, terracotta for 10..12, and for plain 4..9 a
 * class of their own (`v-mid n4`..`v-mid n9`) so each number has its own color, like the physical deck.
 */
export function scoreClass(v: number): string {
  if (v <= 3) return 'v-good';
  if (v <= 9) return `v-mid n${v}`;
  return 'v-cost';
}

export const CardFace = memo(function CardFace({
  value,
  width,
  name: nameOverride,
}: {
  value: number;
  width: number;
  /** Preview a different card name (the /tuner page); defaults to the manifest's name. */
  name?: string;
}) {
  const assets = useAssets();
  const [failed, setFailed] = useState(false);
  const tier = tierFor(width);
  // '' (manifest showName: false) means numeral only: no ribbon, no name line.
  const name = nameOverride ?? cardNameOf(value);
  const art = ART_VALUES.has(value) ? assets.cardArt.get(value) : undefined;
  const showArt = !!art && !failed && tier !== 'mini';
  const layout = tier === 'mini' ? 'mini' : showArt ? 'art' : 'plain';
  const minus5 = value === -5;
  return (
    <div className={`card-face t-${tier} l-${layout} ${scoreClass(value)}${minus5 ? ' v-m5' : ''}${name ? '' : ' no-name'}`} data-face={value} aria-hidden="true">
      {showArt && (
        <img
          className="card-art"
          src={art.src}
          srcSet={srcSet(art)}
          alt=""
          draggable={false}
          loading="eager"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
      {layout === 'plain' && <span className="card-rule" />}
      <span className="card-numeral">{fmt(value)}</span>
      {layout === 'plain' && tier === 'large' && <span className="card-ornament" />}
      {tier === 'large' && name && <span className={layout === 'art' ? 'card-ribbon' : 'card-name'}>{name}</span>}
      {/* An art card without its art still says what it is. */}
      {layout === 'plain' && tier === 'small' && ART_VALUES.has(value) && width >= 64 && name && <span className="card-name small">{name}</span>}
      {minus5 && tier !== 'mini' && <span className="card-frame" />}
    </div>
  );
});

export const CardBack = memo(function CardBack({ width }: { width: number }) {
  const tier = tierFor(width);
  if (tier === 'mini') {
    return (
      <div className="card-back t-mini" aria-hidden="true">
        <span className="back-dot" />
      </div>
    );
  }
  return (
    <div className={`card-back t-${tier}`} aria-hidden="true">
      <span className="back-panel">
        <span className="back-crest">
          {tier === 'large' ? (
            <Shield size={width * 0.5} tone="back" />
          ) : (
            // small backs: the plain shield, crisp at 25-60px (the badge's Tibbs would be a speck)
            <span className="shield">
              <ShieldSvg size={width * 0.5} tone="back" />
            </span>
          )}
        </span>
        {tier === 'large' && <span className="back-motto">{MOTTO}</span>}
      </span>
    </div>
  );
});

/** A two-sided card that flips in 3D when `faceUp` changes. */
export function Card3D({
  value,
  faceUp,
  width,
  flipDelay = 0,
  className = '',
  style,
  flightId,
}: {
  value: number | null;
  faceUp: boolean;
  width: number;
  flipDelay?: number;
  className?: string;
  style?: CSSProperties;
  /** Card id used by the flight animator. */
  flightId?: string;
}) {
  const st = { '--w': `${width}px`, ...style } as CSSProperties;
  return (
    <div
      className={`card3d${faceUp ? ' up' : ''} ${className}`}
      style={st}
      {...(flightId ? { 'data-card-id': flightId } : {})}
      {...(faceUp && value !== null ? { 'data-peek': value } : {})}
    >
      <div className="card3d-inner" style={flipDelay ? { transitionDelay: `${flipDelay}ms` } : undefined}>
        <div className="card3d-side card3d-back">
          <CardBack width={width} />
        </div>
        <div className="card3d-side card3d-front">{value !== null && <CardFace value={value} width={width} />}</div>
      </div>
    </div>
  );
}

/** Flat card (piles, held card, sheets). */
export function FlatCard({
  value,
  width,
  className = '',
  flightId,
  peek = true,
}: {
  value: number | null;
  width: number;
  className?: string;
  flightId?: string;
  peek?: boolean;
}) {
  return (
    <div
      className={`flatcard ${className}`}
      style={{ '--w': `${width}px` } as CSSProperties}
      {...(flightId ? { 'data-card-id': flightId } : {})}
      {...(peek && value !== null ? { 'data-peek': value } : {})}
    >
      {value === null ? <CardBack width={width} /> : <CardFace value={value} width={width} />}
    </div>
  );
}

/** Mini-tier cell for opponents' grids: numeral only, or a lapis back with a gold dot. */
export function MiniCell({
  value,
  faceUp,
  flightId,
  slotKey,
  className = '',
}: {
  value: number | null;
  faceUp: boolean;
  flightId?: string;
  slotKey?: string;
  className?: string;
}) {
  const key = slotKey ? { 'data-slot-key': slotKey } : {};
  if (!faceUp || value === null) return <span className={`mini-cell down ${className}`} {...key} />;
  return (
    <span className={`mini-cell up ${scoreClass(value)} ${className}`} {...key} {...(flightId ? { 'data-card-id': flightId } : {})} data-peek={value}>
      {fmt(value)}
    </span>
  );
}
